import { useBooleanState } from '@cian/react-utils';
import { ModalWindow } from '@cian/ui-kit';

import { FC, useCallback, useState } from 'react';

import { useFetchBookCoworking } from 'shared/hooks/useFetchBookCoworking';
import { useReduxSelector } from 'shared/hooks/useReduxSelector';
import { coworkingIdAsNumberSelector } from 'shared/store/custom';

import { BookCoworkingFrom, TBookCoworkingFormState } from '../BookCoworkingFrom';
import { EBookCoworkingFormFields } from '../BookCoworkingFrom/constants';
import { ModalContentStageFilling } from '../ModalContentStageFilling';
import { ModalContentStageSuccess } from '../ModalContentStageSuccess';

import { EStageTypes, MODAL_MAX_WIDTH, MODAL_WIDTH, formStages, gaObjectByModalType } from './constants';
import { useBookCoworkingModalBoundActions } from './internal/hooks/useBookCoworkingModalBoundActions';
import { normalizeBookCoworkingRequestParams } from './internal/utils/normalizeBookCoworkingRequestParams';
import { HandleStageFillingNext, IBookCoworkingModalProps } from './types';

import * as styles from './BookCoworkingModal.css';

export const BookCoworkingModal: FC<IBookCoworkingModalProps> = props => {
  const { children, formInitialState = {}, analyticParams = {} } = props;

  const boundActions = useBookCoworkingModalBoundActions();

  const coworkingId = useReduxSelector<number>(coworkingIdAsNumberSelector);

  const [bookCoworkingFormState, setBookCoworkingFormState] = useState<TBookCoworkingFormState | null>(null);

  const [formStageState, setFormStageState] = useState<EStageTypes>(EStageTypes.filling);

  const modalVisibilityState = useBooleanState();
  const { setFalse: setFalseModalVisibility, state: isModalVisible } = modalVisibilityState;

  const { callRequest: bookCoworkingCallRequest, pending: bookCoworkingPending } = useFetchBookCoworking();

  const handleStageFillingNext = useCallback<HandleStageFillingNext>(
    formState => {
      setBookCoworkingFormState(formState);

      const bookCoworkingParameters = normalizeBookCoworkingRequestParams(coworkingId, formState);

      bookCoworkingCallRequest(
        { parameters: bookCoworkingParameters },
        {
          onSuccess: () => {
            setFormStageState(formStages[formStageState].next);

            const modalType = formState[EBookCoworkingFormFields.CoworkingOfferType].value;
            boundActions.trackBookingSend({ ...analyticParams, gaObjectType: gaObjectByModalType[modalType] });
          },
        },
      );
    },
    [analyticParams, bookCoworkingCallRequest, boundActions, coworkingId, formStageState],
  );

  const handleModalClose = useCallback<VoidFunction>(() => {
    setFormStageState(EStageTypes.filling);
    setFalseModalVisibility();
    setBookCoworkingFormState(null);
  }, [setFalseModalVisibility]);

  const modalContent = (() => {
    const phoneNumber = bookCoworkingFormState?.phone.value ?? null;

    switch (formStageState) {
      case EStageTypes.filling:
        return (
          <ModalContentStageFilling
            description="Уточните детали, и оператор коворкинга свяжется с вами, чтобы подтвердить бронь. Сейчас вы ни за что не платите."
            title="Бронирование коворкинга"
          >
            <BookCoworkingFrom
              disableFieldsOnPending
              formInitialState={formInitialState}
              pending={bookCoworkingPending}
              onSubmit={handleStageFillingNext}
            />
          </ModalContentStageFilling>
        );

      case EStageTypes.success:
        return (
          <ModalContentStageSuccess
            descriptionType="operator"
            phoneNumber={phoneNumber}
            onNextStage={handleModalClose}
          />
        );

      // istanbul ignore next
      default:
        return null;
    }
  })();

  return (
    <>
      <ModalWindow
        escape
        data-testid="book-coworking-modal"
        fixed={false}
        maxWidth={MODAL_MAX_WIDTH}
        open={isModalVisible}
        outside={formStageState !== EStageTypes.filling}
        size="M"
        width={MODAL_WIDTH}
        onClose={handleModalClose}
      >
        <div className={styles['content-container']} data-testid="book-coworking-modal-content">
          {modalContent}
        </div>
      </ModalWindow>
      {children(modalVisibilityState)}
    </>
  );
};

BookCoworkingModal.displayName = 'BookCoworkingModal';
